import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ArrowBackOutlined from '@mui/icons-material/ArrowBackOutlined';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as LinkDeRota, useNavigate, useParams } from 'react-router-dom';

import { comoErroDaApi, ErroDaApi } from '../../shared/api/erros';
import { formatarCompetencia } from '../../shared/dominio/datas';
import { Identificador } from '../../shared/ui/Identificador';
import { EstadoDeCarregamento } from '../../shared/ui/EstadoDeCarregamento';
import { EstadoDeErro } from '../../shared/ui/EstadoDeErro';
import { chavesDeFaturamento, decidirLinha, opcoesDaConferencia } from './api/faturamento';
import {
  ROTULOS_DE_ACAO,
  type AcaoDeDecisao,
  type Decisao,
  type GuiaDetalhada,
  type LinhaDoVeredito,
  chaveDaDecisao,
} from './api/tipos';
import { SeloDeEstadoDaConferencia } from './SeloDeEstadoDaConferencia';
import { SeloDeEstadoDaLinha } from './SeloDeEstadoDaLinha';

const ACOES: readonly AcaoDeDecisao[] = ['liberar', 'corrigido_no_iw'];

/**
 * A decisão humana de uma linha BARRADA (`TP-052`, T8/T9, `D11`): motivo
 * OBRIGATÓRIO, nunca um envio. Uma linha já decidida mostra quem e quando —
 * decisão é append-only, não se reabre um formulário sobre ela.
 */
function DecisaoDaLinha({
  conferenciaId,
  avaliacaoId,
  linha,
  decisaoExistente,
}: {
  conferenciaId: string;
  avaliacaoId: string;
  linha: number;
  decisaoExistente: Decisao | undefined;
}): React.JSX.Element {
  const cliente = useQueryClient();
  const [acao, definirAcao] = useState<AcaoDeDecisao>('liberar');
  const [motivo, definirMotivo] = useState('');
  const [enviando, definirEnviando] = useState(false);
  const [erro, definirErro] = useState<string | null>(null);

  if (decisaoExistente !== undefined) {
    return (
      <Stack spacing={0.25} aria-label="Decisão registrada">
        <Typography variant="body2">
          {ROTULOS_DE_ACAO[decisaoExistente.acao]} — {decisaoExistente.motivo}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {decisaoExistente.decididoPor ?? 'pessoa não identificada'} em {decisaoExistente.decididoEm}
        </Typography>
      </Stack>
    );
  }

  const podeDecidir = motivo.trim() !== '' && !enviando;

  return (
    <Stack spacing={1} aria-label={`Decidir a linha ${String(linha)}`} sx={{ maxWidth: 360 }}>
      <TextField
        select
        label="Ação"
        size="small"
        value={acao}
        onChange={(evento) => {
          definirAcao(evento.target.value as AcaoDeDecisao);
        }}
      >
        {ACOES.map((valor) => (
          <MenuItem key={valor} value={valor}>
            {ROTULOS_DE_ACAO[valor]}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label="Motivo (obrigatório)"
        size="small"
        value={motivo}
        onChange={(evento) => {
          definirMotivo(evento.target.value);
        }}
        multiline
        minRows={2}
      />
      <Box>
        <Button
          size="small"
          variant="contained"
          disabled={!podeDecidir}
          onClick={() => {
            definirEnviando(true);
            definirErro(null);
            decidirLinha({ conferenciaId, avaliacaoId, linha, acao, motivo: motivo.trim() })
              .then(() => {
                void cliente.invalidateQueries({ queryKey: chavesDeFaturamento.conferencia(conferenciaId) });
                void cliente.invalidateQueries({ queryKey: chavesDeFaturamento.raiz });
              })
              .catch((causa: unknown) => {
                definirErro(comoErroDaApi(causa).message);
              })
              .finally(() => {
                definirEnviando(false);
              });
          }}
        >
          Registrar decisão
        </Button>
      </Box>
      {erro !== null ? (
        <Typography variant="caption" color="error.main" role="alert">
          {erro}
        </Typography>
      ) : null}
    </Stack>
  );
}

function TabelaDaGuia({
  conferenciaId,
  guia,
  decisoesPorChave,
}: {
  conferenciaId: string;
  guia: GuiaDetalhada;
  decisoesPorChave: ReadonlyMap<string, Decisao>;
}): React.JSX.Element {
  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="subtitle2">Guia {guia.referenciaGuia}</Typography>
        <Identificador valor={guia.avaliacaoId} />
      </Stack>
      <TableContainer>
        <Table aria-label={`Linhas da guia ${guia.referenciaGuia}`} size="small">
          <TableHead>
            <TableRow>
              <TableCell>Linha</TableCell>
              <TableCell>Código</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Motivo</TableCell>
              <TableCell>Decisão</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {guia.linhas.map((linhaDoVeredito: LinhaDoVeredito) => (
              <TableRow key={linhaDoVeredito.linha} hover>
                <TableCell>{linhaDoVeredito.linha}</TableCell>
                <TableCell>{linhaDoVeredito.codigo}</TableCell>
                <TableCell>
                  <SeloDeEstadoDaLinha estado={linhaDoVeredito.estado} />
                </TableCell>
                <TableCell>
                  {linhaDoVeredito.motivos.length === 0
                    ? '—'
                    : linhaDoVeredito.motivos.map((motivo, indice) => (
                        <Typography variant="body2" key={indice}>
                          {motivo.mensagem}
                          {motivo.fonte !== null ? (
                            <Typography variant="caption" color="text.secondary" component="span">
                              {' '}
                              ({motivo.fonte})
                            </Typography>
                          ) : null}
                        </Typography>
                      ))}
                </TableCell>
                <TableCell>
                  {linhaDoVeredito.estado === 'barrado' ? (
                    <DecisaoDaLinha
                      conferenciaId={conferenciaId}
                      avaliacaoId={guia.avaliacaoId}
                      linha={linhaDoVeredito.linha}
                      decisaoExistente={decisoesPorChave.get(chaveDaDecisao(guia.avaliacaoId, linhaDoVeredito.linha))}
                    />
                  ) : (
                    '—'
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

/**
 * O detalhe de uma conferência (`TP-052`, T9): veredito por guia e por
 * linha, com a regra e a fonte citadas, e a decisão humana linha a linha. A
 * conferência só aparece como liberada quando não há `BARRADO` em aberto —
 * o estado vem sempre da API, nunca calculado aqui.
 */
export function DetalheDaConferencia(): React.JSX.Element {
  const { conferenciaId } = useParams<{ conferenciaId: string }>();
  const navegar = useNavigate();

  const consulta = useQuery({ ...opcoesDaConferencia(conferenciaId ?? ''), enabled: conferenciaId !== undefined });

  if (conferenciaId === undefined) {
    return (
      <Alert severity="error" role="alert">
        Conferência não informada na rota.
      </Alert>
    );
  }

  if (consulta.isPending) return <EstadoDeCarregamento linhas={6} />;

  if (consulta.isError) {
    if (consulta.error instanceof ErroDaApi && consulta.error.status === 401) {
      void navegar('/entrar', { replace: true });
    }
    return (
      <EstadoDeErro
        erro={consulta.error}
        contexto="Não foi possível carregar a conferência"
        aoReprocessar={() => {
          void consulta.refetch();
        }}
      />
    );
  }

  const conferencia = consulta.data;
  const decisoesPorChave = new Map(conferencia.decisoes.map((d) => [chaveDaDecisao(d.avaliacaoId, d.linha), d]));

  return (
    <Stack spacing={2}>
      <Button component={LinkDeRota} to="/faturamento" startIcon={<ArrowBackOutlined />} sx={{ alignSelf: 'start' }}>
        Voltar para Faturamento
      </Button>

      <Box>
        <Typography variant="h1">Conferência de faturamento</Typography>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
          <Identificador valor={conferencia.id} />
          <SeloDeEstadoDaConferencia estado={conferencia.estado} />
          <Typography variant="body2" color="text.secondary">
            Competência {formatarCompetencia(conferencia.competencia)}
          </Typography>
        </Stack>
      </Box>

      <Alert severity="info" role="status">
        Esta tela nunca envia o lote ao IW nem à operadora: ela só confere e registra a decisão
        humana. O envio é um passo futuro, sempre com um clique separado.
      </Alert>

      {conferencia.estado === 'recusada' && conferencia.motivoRecusa !== null ? (
        <Alert severity="error" role="alert">
          Conferência recusada: {conferencia.motivoRecusa}
        </Alert>
      ) : null}

      {conferencia.estado === 'segurada' ? (
        <Alert severity="warning" role="alert">
          Esta conferência está segurada: existe linha barrada sem decisão. Ela só fica liberada
          quando todas as linhas barradas tiverem decisão registrada.
        </Alert>
      ) : null}

      {conferencia.guias.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nenhuma guia nesta conferência.
        </Typography>
      ) : (
        conferencia.guias.map((guia) => (
          <Paper variant="outlined" sx={{ p: 2 }} key={guia.avaliacaoId}>
            <TabelaDaGuia conferenciaId={conferenciaId} guia={guia} decisoesPorChave={decisoesPorChave} />
          </Paper>
        ))
      )}
    </Stack>
  );
}
